import { gql, useQuery } from "@apollo/client";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import queryString from "query-string";
import React from "react";
import { useLocation } from "react-router-dom";
import { useBranchContext } from "../../../contexts/branchContext";
import useQueryString from "../../../hooks/useQueryString";
import AdminListContainer from "../../../styles/components/AdminListContainer";
import DataTable from "../../common/DataTable";
import Loading from "../../common/Loading";
import Meta from "../../common/Meta";
import Pagination from "../../common/Pagination";
import Search from "../../common/Search";

export const USERGROUPS = gql`
  query Usergroups {
    __type(name: "Usergroup") {
      enumValues {
        name
      }
    }
  }
`;

export const USERS = gql`
  query Users($skip: Int, $take: Int, $where: UserWhereInput) {
    users(skip: $skip, take: $take, where: $where) {
      id
      username
      usergroup
      employee {
        id
        lastName
        firstName
        middleName
      }
      branch {
        id
        name
      }
    }
  }
`;

const USERS_COUNT = gql`
  query UsersCount($where: UserWhereInput) {
    usersCount(where: $where)
  }
`;

const UsersPage = () => {
  const title = `Users`;
  const { branchFilterQueryString } = useBranchContext();
  const paramsObj = queryString.parse(useLocation().search);
  const page = parseInt(paramsObj.page) || 1;
  const perPage = 50;

  const {
    handleChangeQueryString: handleChangeSearch,
    handleSubmitQueryString: handleSubmitSearch,
    queryStringValue: searchTerm,
    initialQueryStringValue: searchQueryString,
  } = useQueryString("search");
  const {
    handleSubmitQueryString: handleSubmitFilter,
    initialQueryStringValue: filterQueryString,
  } = useQueryString("filter");

  const where = {
    ...(filterQueryString && {
      usergroup: {
        equals: filterQueryString,
      },
    }),
    ...(branchFilterQueryString && {
      branch: {
        id: {
          equals: branchFilterQueryString,
        },
      },
    }),
    ...(searchQueryString && {
      OR: [
        {
          username: {
            contains: searchQueryString,
            mode: "insensitive",
          },
        },
        {
          employee: {
            lastName: {
              contains: searchQueryString,
              mode: "insensitive",
            },
          },
        },
        {
          employee: {
            firstName: {
              contains: searchQueryString,
              mode: "insensitive",
            },
          },
        },
      ],
    }),
  };
  const { data: { users } = {}, loading } = useQuery(USERS, {
    fetchPolicy: "network-only",
    variables: {
      skip: page * perPage - perPage,
      take: perPage,
      where,
    },
  });

  const {
    data: { __type: { enumValues: usergroups } = {} } = {},
    loading: usergroupsLoading,
  } = useQuery(USERGROUPS, {
    fetchPolicy: "network-only",
  });

  const columns = [
    {
      header: "Branch",
      accessor: "branch.name",
    },
    {
      header: "Username",
      accessor: "username",
    },
    {
      header: "Name",
      accessor: "employee",
      Cell: (children) =>
        children &&
        `${children?.firstName} ${children?.middleName} ${children?.lastName}`,
    },
    {
      header: "Usergroup",
      accessor: "usergroup",
      Cell: (children) => children && children.replace(/_/g, " "),
    },
  ];
  if (loading || usergroupsLoading) return <Loading />;
  return (
    <>
      <Meta title={`${title} | Meatsumo`} />
      <AdminListContainer>
        <Typography variant="h3" color="primary" gutterBottom>
          {title}
        </Typography>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          alignItems={{ xs: null, md: "flex-end" }}
          spacing={1}
        >
          <FormControl variant="outlined" size="small">
            <InputLabel id="usergroup-label">Usergroup</InputLabel>
            <Select
              labelId="usergroup-label"
              label="Description"
              id="usergroup"
              name="usergroup"
              value={filterQueryString ?? ""}
              onChange={(e) => {
                handleSubmitFilter("direct")(e);
              }}
              sx={{
                backgroundColor: (theme) => theme.palette.common.white,
                minWidth: "300px",
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {usergroups?.map(({ name }) => (
                <MenuItem key={name} value={name}>
                  {name.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Search
            handleChangeSearch={handleChangeSearch}
            handleSubmitSearch={handleSubmitSearch()}
            searchTerm={searchTerm}
          />
        </Stack>
        <Paper className="dataTableContainer">
          <DataTable data={users} columns={columns} />
        </Paper>
        <Pagination
          query={USERS_COUNT}
          variables={{
            where,
          }}
          perPage={perPage}
          page={Number(page)}
        />
      </AdminListContainer>
    </>
  );
};

export default UsersPage;
